/**
 * Tax & Currency Settings Component
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { Loader2, DollarSign, Percent, Save } from 'lucide-react';

const CURRENCY_PRESETS = [
  { code: 'IDR', symbol: 'Rp', position: 'before', decimal: 0, decSep: ',', thousandSep: '.' },
  { code: 'USD', symbol: '$', position: 'before', decimal: 2, decSep: '.', thousandSep: ',' },
  { code: 'EUR', symbol: '€', position: 'before', decimal: 2, decSep: ',', thousandSep: '.' },
  { code: 'GBP', symbol: '£', position: 'before', decimal: 2, decSep: '.', thousandSep: ',' },
  { code: 'SGD', symbol: 'S$', position: 'before', decimal: 2, decSep: '.', thousandSep: ',' },
  { code: 'MYR', symbol: 'RM', position: 'before', decimal: 2, decSep: '.', thousandSep: ',' },
  { code: 'JPY', symbol: '¥', position: 'before', decimal: 0, decSep: '.', thousandSep: ',' },
  { code: 'CNY', symbol: '¥', position: 'before', decimal: 2, decSep: '.', thousandSep: ',' },
];

export function TaxCurrencySettings({ api, outletId }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Tax settings
  const [taxSettings, setTaxSettings] = useState({
    tax_enabled: true,
    tax_name: 'PPN',
    tax_rate: 10,
    tax_inclusive: false,
    service_charge_enabled: false,
    service_charge_name: 'Service',
    service_charge_rate: 5,
  });
  
  // Currency settings
  const [currencySettings, setCurrencySettings] = useState({
    currency_code: 'IDR',
    currency_symbol: 'Rp',
    currency_position: 'before',
    decimal_places: 0,
    decimal_separator: ',',
    thousand_separator: '.',
  });

  useEffect(() => {
    if (outletId) {
      fetchSettings();
    }
  }, [outletId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [taxResp, currencyResp] = await Promise.all([
        api.get('/settings/tax', { params: { outlet_id: outletId } }),
        api.get('/settings/currency', { params: { outlet_id: outletId } }),
      ]);
      setTaxSettings(taxResp.data);
      setCurrencySettings(currencyResp.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTax = async () => {
    setSaving(true);
    try {
      await api.put('/settings/tax', taxSettings, {
        params: { outlet_id: outletId },
      });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      await api.put('/settings/currency', currencySettings, {
        params: { outlet_id: outletId },
      });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCurrencyPreset = (code) => {
    const preset = CURRENCY_PRESETS.find(p => p.code === code);
    if (preset) {
      setCurrencySettings({
        ...currencySettings,
        currency_code: preset.code,
        currency_symbol: preset.symbol,
        currency_position: preset.position,
        decimal_places: preset.decimal,
        decimal_separator: preset.decSep,
        thousand_separator: preset.thousandSep,
      });
    }
  };

  const formatPreview = (amount = 1234567.89) => {
    const { currency_symbol, currency_position, decimal_places, decimal_separator, thousand_separator } = currencySettings;
    
    // Round to decimal places
    const rounded = Number(amount.toFixed(decimal_places));
    
    // Split integer and decimal parts
    const [intPart, decPart] = rounded.toFixed(decimal_places).split('.');
    
    // Add thousand separator
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousand_separator);
    
    // Combine
    let formatted = formattedInt;
    if (decimal_places > 0 && decPart) {
      formatted += decimal_separator + decPart;
    }
    
    // Add currency symbol
    if (currency_position === 'before') {
      return `${currency_symbol} ${formatted}`;
    } else {
      return `${formatted} ${currency_symbol}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tax Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="h-5 w-5 text-orange-600" />
            {t('settings.tax.title')}
          </CardTitle>
          <CardDescription>{t('settings.tax.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable Tax */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">{t('settings.tax.enable')}</Label>
              <p className="text-sm text-muted-foreground">
                {taxSettings.tax_enabled ? t('settings.tax.tax_exclusive_desc') : ''}
              </p>
            </div>
            <Switch
              checked={taxSettings.tax_enabled}
              onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, tax_enabled: checked })}
              data-testid="tax-enabled-switch"
            />
          </div>

          {taxSettings.tax_enabled && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('settings.tax.tax_name')}</Label>
                  <Input
                    value={taxSettings.tax_name}
                    onChange={(e) => setTaxSettings({ ...taxSettings, tax_name: e.target.value })}
                    placeholder={t('settings.tax.tax_name_placeholder')}
                    data-testid="tax-name-input"
                  />
                </div>
                <div>
                  <Label>{t('settings.tax.tax_rate')}</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={taxSettings.tax_rate}
                    onChange={(e) => setTaxSettings({ ...taxSettings, tax_rate: parseFloat(e.target.value) || 0 })}
                    data-testid="tax-rate-input"
                  />
                </div>
              </div>

              {/* Tax Inclusive Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div>
                  <Label className="text-base">{t('settings.tax.tax_inclusive')}</Label>
                  <p className="text-sm text-muted-foreground">
                    {taxSettings.tax_inclusive 
                      ? t('settings.tax.tax_inclusive_desc')
                      : t('settings.tax.tax_exclusive_desc')
                    }
                  </p>
                </div>
                <Switch
                  checked={taxSettings.tax_inclusive}
                  onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, tax_inclusive: checked })}
                  data-testid="tax-inclusive-switch"
                />
              </div>

              {/* Service Charge */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Label className="text-base">{t('settings.tax.enable_service')}</Label>
                    <p className="text-sm text-muted-foreground">{t('settings.tax.service_charge')}</p>
                  </div>
                  <Switch
                    checked={taxSettings.service_charge_enabled}
                    onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, service_charge_enabled: checked })}
                    data-testid="service-enabled-switch"
                  />
                </div>

                {taxSettings.service_charge_enabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('settings.tax.service_name')}</Label>
                      <Input
                        value={taxSettings.service_charge_name}
                        onChange={(e) => setTaxSettings({ ...taxSettings, service_charge_name: e.target.value })}
                        data-testid="service-name-input"
                      />
                    </div>
                    <div>
                      <Label>{t('settings.tax.service_rate')}</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={taxSettings.service_charge_rate}
                        onChange={(e) => setTaxSettings({ ...taxSettings, service_charge_rate: parseFloat(e.target.value) || 0 })}
                        data-testid="service-rate-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <Button onClick={handleSaveTax} disabled={saving} className="w-full" data-testid="save-tax-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('common.save')}
          </Button>
        </CardContent>
      </Card>

      {/* Currency Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            {t('settings.currency.title')}
          </CardTitle>
          <CardDescription>{t('settings.currency.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Currency Preset */}
          <div>
            <Label>{t('settings.currency.currency_code')}</Label>
            <Select
              value={currencySettings.currency_code}
              onValueChange={handleCurrencyPreset}
            >
              <SelectTrigger data-testid="currency-code-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCY_PRESETS.map((preset) => (
                  <SelectItem key={preset.code} value={preset.code}>
                    {preset.code} ({preset.symbol})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('settings.currency.currency_symbol')}</Label>
              <Input
                value={currencySettings.currency_symbol}
                onChange={(e) => setCurrencySettings({ ...currencySettings, currency_symbol: e.target.value })}
                data-testid="currency-symbol-input"
              />
            </div>
            <div>
              <Label>{t('settings.currency.position')}</Label>
              <Select
                value={currencySettings.currency_position}
                onValueChange={(v) => setCurrencySettings({ ...currencySettings, currency_position: v })}
              >
                <SelectTrigger data-testid="currency-position-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">{t('settings.currency.position_before')}</SelectItem>
                  <SelectItem value="after">{t('settings.currency.position_after')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>{t('settings.currency.decimal_places')}</Label>
              <Select
                value={String(currencySettings.decimal_places)}
                onValueChange={(v) => setCurrencySettings({ ...currencySettings, decimal_places: parseInt(v) })}
              >
                <SelectTrigger data-testid="decimal-places-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('settings.currency.decimal_separator')}</Label>
              <Select
                value={currencySettings.decimal_separator}
                onValueChange={(v) => setCurrencySettings({ ...currencySettings, decimal_separator: v })}
              >
                <SelectTrigger data-testid="decimal-sep-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=".">. (dot)</SelectItem>
                  <SelectItem value=",">, (comma)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('settings.currency.thousand_separator')}</Label>
              <Select
                value={currencySettings.thousand_separator}
                onValueChange={(v) => setCurrencySettings({ ...currencySettings, thousand_separator: v })}
              >
                <SelectTrigger data-testid="thousand-sep-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value=".">. (dot)</SelectItem>
                  <SelectItem value=",">, (comma)</SelectItem>
                  <SelectItem value=" ">space</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 rounded-lg bg-muted/50">
            <Label className="text-muted-foreground">{t('settings.currency.preview')}</Label>
            <div className="mt-2 text-2xl font-bold text-primary">
              {formatPreview(1234567.89)}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {formatPreview(50000)} | {formatPreview(999.99)} | {formatPreview(1000000)}
            </div>
          </div>

          <Button onClick={handleSaveCurrency} disabled={saving} className="w-full" data-testid="save-currency-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('common.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default TaxCurrencySettings;
